import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Car, Mail, CheckCircle2, Bookmark } from "lucide-react";

export default function AdminOverview() {
  const [vehicles, setVehicles] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}),
      api.get("/admin/inquiries").then((r) => setInquiries(r.data || [])).catch(() => {}),
    ]);
  }, []);

  const available = vehicles.filter((v) => v.status === "available").length;
  const reserved = vehicles.filter((v) => v.status === "reserved").length;
  const sold = vehicles.filter((v) => v.status === "sold").length;
  const newCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <div data-testid="admin-overview">
      <div className="section-label mb-3">Overview</div>
      <h1 className="font-display text-3xl md:text-4xl font-light">Dashboard</h1>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-[1px] bg-white/5">
        <Stat icon={<Car size={15} />} label="Total vehicles" value={vehicles.length} />
        <Stat icon={<CheckCircle2 size={15} />} label="Available" value={available} accent />
        <Stat icon={<Bookmark size={15} />} label="Reserved" value={reserved} />
        <Stat icon={<Mail size={15} />} label="New inquiries" value={newCount} accent />
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-white/5 bg-[#0F0F0F] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl">Recent inquiries</h2>
            <Link to="/admin/inquiries" className="text-[10px] tracking-[0.3em] uppercase text-[#C6A15B]">View all</Link>
          </div>
          <div className="divide-y divide-white/5">
            {inquiries.slice(0, 5).map((i) => (
              <div key={i.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-white">{i.name}</div>
                  <div className="text-xs text-zinc-500">{i.car_preference}</div>
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{i.status}</div>
              </div>
            ))}
            {inquiries.length === 0 && <div className="text-zinc-500 text-sm py-4">No inquiries yet.</div>}
          </div>
        </div>

        <div className="border border-white/5 bg-[#0F0F0F] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl">Garage status</h2>
            <Link to="/admin/vehicles" className="text-[10px] tracking-[0.3em] uppercase text-[#C6A15B]">Manage</Link>
          </div>
          <div className="space-y-3">
            <Bar label="Available" value={available} total={Math.max(vehicles.length, 1)} color="#34D399" />
            <Bar label="Reserved" value={reserved} total={Math.max(vehicles.length, 1)} color="#C6A15B" />
            <Bar label="Sold" value={sold} total={Math.max(vehicles.length, 1)} color="#52525B" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="bg-[#0B0B0B] p-6">
      <div className={`flex items-center gap-2 ${accent ? "text-[#C6A15B]" : "text-zinc-400"}`}>{icon}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-3">{label}</div>
      <div className="font-display text-3xl text-white mt-1">{value}</div>
    </div>
  );
}

function Bar({ label, value, total, color }) {
  const pct = Math.round((value / total) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-zinc-400 tracking-wider">{label}</span>
        <span className="text-white">{value}</span>
      </div>
      <div className="h-1 bg-white/5">
        <div className="h-1" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
